import { useAppStore } from '@core/store'
import { referencialDoModelo, type Referencial } from '@modules/bcf/lib/bcfCoordenadas'
import {
  IDENTITY_PLACEMENT,
  modelRecentre,
  placementsDiffer,
  type GeoPlacement,
} from '@viewer/lib/geoTransform'
import { viewerApi } from '@viewer/lib/viewerApi'

// ---------------------------------------------------------------------------
// Em que coordenadas sai o BCF dos conflitos.
//
// O mundo do viewer e o do mapa: um modelo com IfcMapConversion entra
// colocado por ela, um modelo com as coordenadas embutidas nos
// placements (LoGeoRef 30, sem MapConversion) entra como esta. Numa
// federacao como a do Cha139 (ARQ, MAC, HID e EEL em PT-TM06 embutido,
// EST na origem com MapConversion) o mundo e as coordenadas dos
// ficheiros sem MapConversion coincidem; as do EST, essas, sao locais.
//
// O BCF escreve-se nas coordenadas de UM ficheiro, e as outras
// plataformas leem-no nesse referencial. Antes ia o do modelo activo:
// com o EST activo, a camara do CHA139-001 saia em (53; 46; 84), a cerca
// de 170 km do ARQ noutra ferramenta. A regra agora nao depende do
// modelo activo:
//
//   1. se houver modelos sem MapConversion, o do primeiro deles (o
//      ficheiro e o mundo coincidem);
//   2. se todos tiverem MapConversion, o CRS (o mundo georreferenciado);
//   3. sem georreferenciacao nenhuma, o do modelo, como antes (e o
//      mesmo que o mundo).
//
// O utilizador pode escolher outro modelo na exportacao.
// ---------------------------------------------------------------------------

export interface ModeloGeo {
  id: string
  fileName: string
  /** Hash do ficheiro, para casar com as `model_refs` de um viewpoint
   *  (ver bcfReferencial.ts). */
  sha256?: string
  /** Placement efectivo (override do utilizador ou o do
   *  IfcMapConversion); null quando nao se sabe. */
  placement: GeoPlacement | null
  /** CRS declarado (ex.: "EPSG:3763"), ou null. */
  crs: string | null
  /** Referencial do modelo carregado (ver bcfCoordenadas). */
  referencial: Referencial | null
}

export interface EscolhaDeReferencial {
  /** Modelo cujo ficheiro da as coordenadas; null = o mundo (CRS). */
  modeloId: string | null
  referencial: Referencial | null
  /** Para a linha "Coordenadas BCF no referencial de: ...". */
  rotulo: string
}

/** O modelo foi colocado por um MapConversion (ou por um placement do
 *  utilizador)? Entao as coordenadas do ficheiro nao sao as do mundo. */
export function temConversao(m: ModeloGeo): boolean {
  return m.placement !== null && placementsDiffer(m.placement, IDENTITY_PLACEMENT)
}

/** Aplica a regra (ou a escolha do utilizador, quando e um modelo
 *  carregado). Pura: os testes passam modelos falsos. */
export function escolherReferencial(
  modelos: readonly ModeloGeo[],
  escolha: string | null = null,
): EscolhaDeReferencial {
  const crs = modelos.find((m) => m.crs)?.crs ?? null
  const comCrs = crs ? ` (CRS ${crs})` : ''
  const escolhido = escolha ? modelos.find((m) => m.id === escolha) : undefined
  if (escolhido) {
    return {
      modeloId: escolhido.id,
      referencial: escolhido.referencial,
      rotulo: temConversao(escolhido)
        ? `${escolhido.fileName} (coordenadas locais do ficheiro)`
        : `${escolhido.fileName}${comCrs}`,
    }
  }
  const semConversao = modelos.find((m) => !temConversao(m))
  if (semConversao) {
    return {
      modeloId: semConversao.id,
      referencial: semConversao.referencial,
      rotulo: `${semConversao.fileName}${comCrs}`,
    }
  }
  if (modelos.length > 0) {
    // Todos colocados por MapConversion: o mundo e o CRS.
    return {
      modeloId: null,
      referencial: null,
      rotulo: crs ? `CRS ${crs} (mundo georreferenciado)` : 'mundo georreferenciado',
    }
  }
  return { modeloId: null, referencial: null, rotulo: 'referencial da cena' }
}

/** Os modelos carregados, com o placement, o CRS e o referencial. */
export function modelosGeoDaCena(): ModeloGeo[] {
  const s = useAppStore.getState()
  return s.models.map((m) => {
    const placement = s.geoOverrides?.[m.id] ?? s.geoBaselines?.[m.id] ?? null
    const crs = s.crsOverrides?.[m.id] ?? s.crsMeta?.[m.id] ?? null
    const model = viewerApi.getModel(m.id)
    let referencial: Referencial | null = null
    if (model) {
      model.object.updateMatrixWorld(true)
      referencial = referencialDoModelo(model.object.matrixWorld, modelRecentre(model))
    }
    return { id: m.id, fileName: m.fileName, sha256: m.sha256, placement, crs, referencial }
  })
}
